import {
  Button,
  ButtonGroup,
  closeCurrentDialog,
  DataStatus,
  Form,
  FormButton,
  FormSubmitHandler,
  showNotification,
} from 'platform/components';
import {Box, Grid, Heading, Icon, Text, VStack} from 'platform/foundation';

import {DeepPartial} from 'react-hook-form';

import {isNilOrEmpty} from 'ramda-adjunct';

import {useSendCustomerSmsMutation} from '@dms/api/core';
import {useGetCustomerV2Query} from '@dms/api/customer';
import {EntityResourceIds} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {
  getCustomerPhoneNumber,
  handleApiError,
  SmsCustomerSelectField as CustomerSelectField,
  SmsMessageFormField as MessageFormField,
  SmsMessagePreview,
  useGetStructuredPhoneNumber,
  type SmsFormValues as FormValues,
  SendCustomerSmsFormSchema,
} from '@dms/shared';

import {Nullish, suffixTestId, TestIdProps} from 'shared/web';

import {useGetDefaultCustomerOptions} from '../SendAftersalesEmailForm/hooks/useGetDefaultCustomerOptions';

interface SentSmsToAfterSalesCustomerFormProps extends TestIdProps {
  serviceCaseId: string;
  recordId?: string | Nullish;
  resourceId?: EntityResourceIds | Nullish;
  serviceOrderId?: string | Nullish;
}

export function SendAfterSalesCustomerSmsForm(props: SentSmsToAfterSalesCustomerFormProps) {
  const {
    defaultCustomerOptions,
    customerId,
    isLoading: isLoadingDefaultCustomerOptions,
  } = useGetDefaultCustomerOptions({
    serviceCaseId: props.serviceCaseId,
    serviceOrderId: props.serviceOrderId,
  });

  const {
    data: customer,
    isLoading: isLoadingCustomer,
    isError: isCustomerError,
  } = useGetCustomerV2Query(
    {
      customerId: customerId ?? '',
    },
    {skip: isNilOrEmpty(customerId)}
  );

  const getStructuredPhoneNumber = useGetStructuredPhoneNumber();
  const [sendCustomerSms] = useSendCustomerSmsMutation();

  const handleSubmit: FormSubmitHandler<FormValues> = async (data) => {
    const phoneNumber = getStructuredPhoneNumber(data.phoneNumber);

    await sendCustomerSms({
      sendCustomerSmsRequestBody: {
        body: data.body ?? '',
        customerId: data.customerId,
        resourceId: props.resourceId,
        recordId: props.recordId,
        customerPhoneNumber: phoneNumber,
      },
    })
      .unwrap()
      .then(() => showNotification.success(i18n.t('entity.customer.labels.smsSuccessfullySend')))
      .then(closeCurrentDialog)
      .catch(handleApiError);
  };

  const isLoading = isLoadingCustomer || isLoadingDefaultCustomerOptions;
  const isError = isCustomerError;

  const defaultValues: DeepPartial<FormValues> = {
    customerId: customerId ?? undefined,
    phoneNumber: getCustomerPhoneNumber(customer) ?? undefined,
  };

  return (
    <DataStatus isLoading={isLoading} isError={isError}>
      <Form<FormValues>
        defaultValues={defaultValues}
        onSubmit={handleSubmit}
        experimentalZodSchema={SendCustomerSmsFormSchema}
      >
        {(control, formApi) => (
          <VStack spacing={4}>
            <Grid columns={2}>
              <VStack spacing={2}>
                <Box padding={10}>
                  <VStack spacing={4} align="center" justify="center">
                    <Icon
                      value="action/send_to_mobile"
                      color="palettes.blue.30.100"
                      isNotFilled
                      size={20}
                      data-testid={suffixTestId('messageIcon', props)}
                    />

                    <Heading size={3}>
                      {i18n.t('entity.customer.labels.customer_message_title_sms')}
                    </Heading>
                    <Text color="secondary" align="center">
                      {i18n.t('entity.customer.labels.customer_message_title_description_sms')}
                    </Text>
                  </VStack>
                </Box>

                <CustomerSelectField
                  control={control}
                  formApi={formApi}
                  data-testid={suffixTestId('customerSelectField', props)}
                  defaultOptions={defaultCustomerOptions}
                />

                <MessageFormField
                  control={control}
                  formApi={formApi}
                  resourceId={props.resourceId}
                  service={{
                    serviceCaseId: props.serviceCaseId,
                    serviceOrderId: props.serviceOrderId,
                  }}
                  recordId={props.recordId}
                  data-testid={suffixTestId('messageFormField', props)}
                />
              </VStack>

              <SmsMessagePreview
                control={control}
                data-testid={suffixTestId('smsMessagePreview', props)}
              />
            </Grid>

            <ButtonGroup align="right">
              <Button
                variant="secondary"
                title={i18n.t('general.actions.cancel')}
                onClick={closeCurrentDialog}
                data-testid={suffixTestId('cancelButton', props)}
              />
              <FormButton
                type="submit"
                control={control}
                title={i18n.t('general.actions.send')}
                data-testid={suffixTestId('sendButton', props)}
              />
            </ButtonGroup>
          </VStack>
        )}
      </Form>
    </DataStatus>
  );
}
